import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  UsersRound,
  MapPin,
  MessageCircle,
  User,
  LogOut,
  Keyboard,
  X,
  Sparkles,
  Filter,
  Compass,
  CalendarDays,
  Star,
  Inbox,
  Settings,
  Bell,
  Music,
  RefreshCw,
  Radar,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSignOutAndGoHome } from "@/hooks/useSignOutAndGoHome";
import { logClientError } from "@/lib/error-logger";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { prefetchRoute, prefetchRoutesIdle } from "@/lib/routePrefetch";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useShellNav } from "@/components/layouts/ShellNavContext";


const SHORTCUTS_HIDDEN_KEY = "zappy.bottomnav.shortcuts.hidden";
const LONG_PRESS_MS = 500;

type QuickAction = {
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  onSelect: (navigate: ReturnType<typeof useNavigate>) => void | Promise<void>;
  /** Feature flag obrigatória. Se desligada, a ação é ocultada. */
  flag?: string;
};

type NavItem = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path: string;
  hint: string;
  actions: QuickAction[];
  /** Feature flag obrigatória. Se desligada, a entrada é ocultada. */
  flag?: string;
  /**
   * Rotas adicionais que pertencem ao mesmo módulo. Enquanto o usuário estiver
   * em qualquer uma delas, o item permanece destacado (interesses recebidos
   * e chat fazem parte da área de conexões).
   */
  modulePaths?: string[];
};


const allNavItems: NavItem[] = [
  {
    icon: Compass,
    label: "Descobrir",
    path: "/discover",
    hint: "Explore sua região ou descubra pessoas no mesmo local.",
    flag: "discovery_enabled",
    // /nearby abre o Descobrir já no modo "Perto de mim" (pessoas por distância);
    // /nearby-places é a exploração regional de locais/eventos, sem pessoas.
    modulePaths: ["/nearby", "/nearby-places"],
    actions: [
      { label: "Abrir Descobrir", description: "Ir para a tela principal de perfis.", icon: Compass, onSelect: (n) => n("/discover") },
      { label: "Perto de mim", description: "Pessoas ativas ao seu redor agora, por distância.", icon: Radar, onSelect: (n) => n("/nearby") },
      { label: "Locais e eventos próximos", description: "Estabelecimentos e eventos perto de você.", icon: MapPin, onSelect: (n) => n("/nearby-places") },
      { label: "Interesses recebidos", description: "Ver pessoas que já demonstraram interesse.", icon: Sparkles, onSelect: (n) => n("/who-liked-you") },
      { label: "Destaques de hoje", description: "Seleção diária de quem mais combina com você.", icon: Sparkles, onSelect: (n) => n("/destaques") },
      { label: "Ajustar filtros", description: "Refinar idade, intenção e preferências.", icon: Filter, onSelect: (n) => n("/discover?openFilters=1") },
    ],
  },

  {
    icon: MapPin,
    label: "Locais",
    path: "/venues",
    hint: "Explore estabelecimentos, restaurantes, eventos e outros locais perto de você.",
    modulePaths: ["/events", "/venue"],

    actions: [
      { label: "Explorar locais", description: "Ver todos os estabelecimentos próximos.", icon: MapPin, onSelect: (n) => n("/venues") },
      { label: "Eventos", description: "Calendário de eventos dos locais parceiros.", icon: CalendarDays, onSelect: (n) => n("/events"), flag: "events_enabled" },
      { label: "Favoritos", description: "Locais que você marcou como favoritos.", icon: Star, onSelect: (n) => n("/venues?tab=favorites") },
      { label: "Meus pedidos de música", description: "Acompanhar situação, resposta e histórico.", icon: Music, onSelect: (n) => n("/meus-pedidos-musica") },
      { label: "Meus avisos dos estabelecimentos", description: "Pedidos, respostas, chat público e eventos.", icon: Bell, onSelect: (n) => n("/meus-avisos-bares") },
      { label: "Ativar avisos no celular", description: "Autorizar notificações e registrar este aparelho.", icon: Bell, onSelect: (n) => n("/configurar-avisos") },
    ],
  },
  {
    icon: MessageCircle,
    label: "Conexões",
    path: "/matches",
    hint: "Acesse suas conversas e novas conexões.",
    modulePaths: ["/who-liked-you", "/chat"],

    actions: [
      { label: "Ver conexões", description: "Lista completa de conexões e conversas.", icon: Inbox, onSelect: (n) => n("/matches") },
      { label: "Interesses recebidos", description: "Pessoas que demonstraram interesse no seu perfil.", icon: UsersRound, onSelect: (n) => n("/who-liked-you") },
      { label: "Notificações", description: "Abrir o centro de notificações.", icon: Bell, onSelect: (n) => n("/profile?openNotifications=1") },
    ],
  },
  {
    icon: User,
    label: "Perfil",
    path: "/profile",
    hint: "Edite seu perfil, fotos e preferências.",
    actions: [
      { label: "Meu perfil", description: "Ver e editar suas informações.", icon: User, onSelect: (n) => n("/profile") },
      { label: "Minha assinatura", description: "Gerenciar plano e benefícios.", icon: Star, onSelect: (n) => n("/my-subscription") },
      { label: "Configurações", description: "Preferências e privacidade.", icon: Settings, onSelect: (n) => n("/profile?tab=settings") },
    ],
  },
];

const BottomNav = React.forwardRef<HTMLElement, Record<string, never>>((_props, ref) => {
  const renderedByShell = useShellNav();
  const location = useLocation();

  const navigate = useNavigate();
  const { user } = useAuth();
  const handleLogout = useSignOutAndGoHome();
  const { isEnabled: isFlagEnabled } = useFeatureFlags();
  // Filtra entradas e ações cuja feature flag está desligada no admin.
  const navItems = React.useMemo<NavItem[]>(
    () =>
      allNavItems
        .filter((it) => !it.flag || isFlagEnabled(it.flag, true))
        .map((it) => ({
          ...it,
          actions: it.actions.filter((a) => !a.flag || isFlagEnabled(a.flag, true)),
        })),
    [isFlagEnabled],
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const [shortcutsHidden, setShortcutsHidden] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(SHORTCUTS_HIDDEN_KEY) === "1";
  });
  const [activeSheet, setActiveSheet] = useState<NavItem | "logout" | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);

  // A11y: guarda o gatilho que abriu o menu para devolver o foco ao fechar
  // (o Sheet é aberto programaticamente, então o Radix não tem trigger).
  const sheetOpenerRef = useRef<HTMLElement | null>(null);
  const menuListRef = useRef<HTMLUListElement | null>(null);

  const openSheet = useCallback((target: NavItem | "logout", opener?: HTMLElement | null) => {
    sheetOpenerRef.current = opener ?? null;
    setActiveSheet(target);
  }, []);

  const closeSheet = useCallback(() => {
    setActiveSheet(null);
    const opener = sheetOpenerRef.current;
    sheetOpenerRef.current = null;
    // devolve o foco ao item da barra que abriu o menu
    requestAnimationFrame(() => opener?.focus?.());
  }, []);

  /** Abre o menu de ações rápidas via teclado (Seta para cima, Alt+Enter ou tecla de menu). */
  const handleNavKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>, target: NavItem | "logout") => {
      const isOpenKey =
        e.key === "ArrowUp" ||
        e.key === "ContextMenu" ||
        ((e.key === "Enter" || e.key === " ") && e.altKey);
      if (!isOpenKey) return;
      e.preventDefault();
      openSheet(target, e.currentTarget);
    },
    [openSheet],
  );

  /** Navegação com setas dentro da lista de ações rápidas (padrão de menu). */
  const handleMenuKeyDown = useCallback((e: React.KeyboardEvent<HTMLUListElement>) => {
    const keys = ["ArrowDown", "ArrowUp", "Home", "End"];
    if (!keys.includes(e.key)) return;
    const items = Array.from(
      menuListRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;
    e.preventDefault();
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    let next = 0;
    if (e.key === "ArrowDown") next = current < 0 ? 0 : (current + 1) % items.length;
    else if (e.key === "ArrowUp") next = current <= 0 ? items.length - 1 : current - 1;
    else if (e.key === "End") next = items.length - 1;
    items[next]?.focus();
  }, []);

  const dismissShortcuts = useCallback(() => {
    setShortcutsHidden(true);
    try {
      window.localStorage.setItem(SHORTCUTS_HIDDEN_KEY, "1");
    } catch {
      /* ignore storage errors */
    }
  }, []);

  // PERF: usa RPC indexada (get_my_unread_count) em vez de COUNT exact sobre
  // toda a tabela chat_messages. Evita full scan + count duplo do PostgREST.
  const fetchUnread = useCallback(async () => {
    if (!user) return;
    // Aba oculta: não gasta rede/CPU/bateria — revalida no retorno.
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    try {
      const { data, error } = await supabase.rpc("get_my_unread_count" as never);
      if (!error && typeof data === "number") setUnreadCount(data);
    } catch {
      /* badge informativa: falha de rede nao quebra a navegacao */
    }
  }, [user]);

  // Debounce maior (1.5s): eventos de chat chegam em rajada; a badge não
  // precisa de precisão em tempo real sub-segundo.
  const debouncedFetch = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchUnread, 1500);
  }, [fetchUnread]);

  useEffect(() => {
    fetchUnread();
    if (!user) return;

    const channel = supabase
      .channel("unread-nav")
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "chat_messages",
      }, debouncedFetch)
      .subscribe();

    const onVisible = () => {
      if (document.visibilityState === "visible") void fetchUnread();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      document.removeEventListener("visibilitychange", onVisible);
      supabase.removeChannel(channel);
    };
  }, [user, fetchUnread, debouncedFetch]);



  // Atalhos de teclado (Alt+1..N para navegar, Alt+0 para sair) — desktop
  useEffect(() => {
    const isTypingTarget = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
    };

    const handleKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (isTypingTarget(e.target)) return;

      const key = e.key;
      if (key >= "1" && key <= String(navItems.length)) {
        const item = navItems[Number(key) - 1];
        if (item) {
          e.preventDefault();
          navigate(item.path);
        }
      } else if (key === "0") {
        e.preventDefault();
        handleLogout();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [navigate, handleLogout, navItems]);

  // Long-press helpers (toque e segure para abrir ações rápidas)
  const startLongPress = useCallback((target: NavItem | "logout", opener?: HTMLElement | null) => {
    longPressFiredRef.current = false;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setActiveSheet(target);
      sheetOpenerRef.current = opener ?? null;
      // Vibração leve em dispositivos compatíveis
      if (typeof navigator !== "undefined" && "vibrate" in navigator) {
        try {
          navigator.vibrate?.(15);
        } catch {
          /* ignore */
        }
      }
    }, LONG_PRESS_MS);
  }, []);

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleClickGuard = useCallback((e: React.MouseEvent, item?: NavItem) => {
    // Se um long-press abriu o sheet, suprimir o clique/navegação que vem depois.
    if (longPressFiredRef.current) {
      e.preventDefault();
      e.stopPropagation();
      longPressFiredRef.current = false;
      return;
    }

    // Log rastreável para qualquer clique de item (especialmente "Descobrir")
    if (item) {
      try {
        const ctx = {
          source: "BottomNav.click",
          target: item.label,
          targetPath: item.path,
          fromRoute: location.pathname,
          userId: user?.id ?? null,
          authenticated: !!user,
          timestamp: new Date().toISOString(),
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        };
        // Breadcrumb visível no console (não é erro)
        console.info(`[BottomNav] click → ${item.path}`, ctx);
      } catch (err) {
        // Falha ao logar não deve bloquear navegação — registra como warn
        void logClientError({
          message: `BottomNav click logging failed: ${(err as Error)?.message ?? "unknown"}`,
          stack: (err as Error)?.stack,
          severity: "warn",
          metadata: {
            source: "BottomNav.click",
            target: item.label,
            targetPath: item.path,
            fromRoute: location.pathname,
            userId: user?.id ?? null,
          },
        });
      }
    }
  }, [location.pathname, user]);

  const handleContextMenu = useCallback((e: React.MouseEvent<HTMLElement>, target: NavItem | "logout") => {
    // No desktop, botão direito também abre o menu rápido.
    e.preventDefault();
    openSheet(target, e.currentTarget);
  }, [openSheet]);

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  // Prefetch dos chunks dos destinos principais quando o navegador estiver
  // ocioso — reduz a espera ao trocar de aba na BottomNav.
  useEffect(() => {
    prefetchRoutesIdle(navItems.map((i) => i.path));
  }, []);

  // Publica altura real do BottomNav como CSS var para outros overlays (ex.: CookieConsent) não sobreporem
  const navRef = React.useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const setVar = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--zappy-bottomnav-offset", `${Math.round(h + 12)}px`);
    };
    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    window.addEventListener("resize", setVar);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setVar);
      document.documentElement.style.removeProperty("--zappy-bottomnav-offset");
    };
  }, []);

  const handlePrefetch = useCallback((path: string) => {
    prefetchRoute(path);
  }, []);

  const sheetItem = typeof activeSheet === "object" && activeSheet !== null ? activeSheet : null;
  const sheetIsLogout = activeSheet === "logout";
  const sheetItemLabel = sheetItem?.label ?? null;

  // Um layout persistente (ProfileShell) já monta a barra: evita duplicidade.
  if (renderedByShell) return null;


  return (
    <>
      <nav ref={(el) => { navRef.current = el; if (typeof ref === "function") ref(el); else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = el; }} className="fixed bottom-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-xl border-t border-border pb-[env(safe-area-inset-bottom,0px)] pl-[env(safe-area-inset-left,0px)] pr-[env(safe-area-inset-right,0px)]" role="navigation" aria-label="Menu principal">
        <TooltipProvider delayDuration={200}>
          {!shortcutsHidden && (
            <div
              className="hidden md:block border-b border-border/60 bg-muted/40"
              role="region"
              aria-label="Atalhos do menu"
            >
              <div className="max-w-lg md:max-w-2xl mx-auto flex items-center justify-between gap-3 px-4 py-1.5">
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <Keyboard className="w-3.5 h-3.5" aria-hidden="true" />
                  <span className="font-medium">Atalhos do menu:</span>
                  <ul className="flex items-center gap-2 flex-wrap">
                    {navItems.map((item, index) => (
                      <li key={item.path} className="flex items-center gap-1">
                        <kbd className="inline-flex items-center rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-mono text-foreground">
                          Alt+{index + 1}
                        </kbd>
                        <span>{item.label}</span>
                      </li>
                    ))}
                    <li className="flex items-center gap-1">
                      <kbd className="inline-flex items-center rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-mono text-foreground">
                        Alt+0
                      </kbd>
                      <span>Sair</span>
                    </li>
                  </ul>
                  <span className="hidden lg:inline text-muted-foreground/80">• Toque e segure para ações rápidas</span>
                </div>
                <button
                  type="button"
                  onClick={dismissShortcuts}
                  aria-label="Ocultar dica de atalhos do menu"
                  className="text-muted-foreground hover:text-foreground transition-colors rounded p-1 inline-flex items-center justify-center min-h-11 min-w-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <X className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-around max-w-lg md:max-w-2xl mx-auto py-3 px-4 md:py-3.5">
            {navItems.map((item, index) => {
              const inPath = (p: string) =>
                location.pathname === p || location.pathname.startsWith(p + "/");
              const isActive = inPath(item.path) || (item.modulePaths?.some(inPath) ?? false);

              const showBadge = item.path === "/matches" && unreadCount > 0;
              const shortcut = `Alt+${index + 1}`;
              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.path}
                      onClick={(e) => handleClickGuard(e, item)}
                      onContextMenu={(e) => handleContextMenu(e, item)}
                      onMouseEnter={() => handlePrefetch(item.path)}
                      onFocus={() => handlePrefetch(item.path)}
                      onTouchStart={(e) => { handlePrefetch(item.path); startLongPress(item, e.currentTarget); }}
                      onTouchEnd={cancelLongPress}
                      onTouchMove={cancelLongPress}
                      onTouchCancel={cancelLongPress}
                      onPointerDown={(e) => {
                        if (e.pointerType === "mouse") return;
                        startLongPress(item, e.currentTarget);
                      }}
                      onPointerUp={cancelLongPress}
                      onPointerLeave={cancelLongPress}
                      onPointerCancel={cancelLongPress}
                      onKeyDown={(e) => handleNavKeyDown(e, item)}
                      aria-label={`${item.label}${showBadge ? ` (${unreadCount} não lidas)` : ""} — ${item.hint} (atalho ${shortcut}; Alt+Enter ou seta para cima abre as ações rápidas)`}
                      aria-keyshortcuts={`${shortcut} Alt+Enter ArrowUp`}
                      aria-haspopup="menu"
                      aria-expanded={sheetItemLabel === item.label}
                      aria-current={isActive ? "page" : undefined}
                      className={`group flex flex-col items-center justify-center gap-1 min-h-11 min-w-11 sm:min-w-16 transition-all duration-200 relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl p-1 sm:px-3 select-none [-webkit-touch-callout:none] ${
                        isActive ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                      }`}
                    >
                      <div className="relative">
                        <item.icon aria-hidden="true" className={`w-5 h-5 transition-transform duration-200 ${isActive ? "fill-primary/20 scale-105" : "group-hover:scale-105"}`} />
                        {showBadge && (
                          <div className="absolute -top-1 -right-1.5 w-4 h-4 rounded-full gradient-warm flex items-center justify-center" aria-hidden="true">
                            <span className="text-[10px] text-primary-foreground font-bold">
                              {unreadCount > 9 ? "9+" : unreadCount}
                            </span>
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] font-body font-medium">{item.label}</span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="top" sideOffset={8} className="max-w-[220px] text-center">
                    <div className="flex items-center justify-center gap-2">
                      <p className="font-semibold text-xs">{item.label}</p>
                      <kbd className="hidden sm:inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                        {shortcut}
                      </kbd>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.hint}</p>
                    <p className="text-[10px] text-muted-foreground/80 mt-1">Toque e segure para ações rápidas</p>
                  </TooltipContent>
                </Tooltip>
              );
            })}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    if (longPressFiredRef.current) {
                      e.preventDefault();
                      longPressFiredRef.current = false;
                      return;
                    }
                    handleLogout();
                  }}
                  onContextMenu={(e) => handleContextMenu(e, "logout")}
                  onTouchStart={(e) => startLongPress("logout", e.currentTarget)}
                  onTouchEnd={cancelLongPress}
                  onTouchMove={cancelLongPress}
                  onTouchCancel={cancelLongPress}
                  onPointerDown={(e) => {
                    if (e.pointerType === "mouse") return;
                    startLongPress("logout", e.currentTarget);
                  }}
                  onPointerUp={cancelLongPress}
                  onPointerLeave={cancelLongPress}
                  onPointerCancel={cancelLongPress}
                  onKeyDown={(e) => handleNavKeyDown(e, "logout")}
                  aria-label="Sair e trocar perfil — Encerra sua sessão atual e volta para a tela inicial. (atalho Alt+0; Alt+Enter ou seta para cima abre as ações rápidas)"
                  aria-keyshortcuts="Alt+0 Alt+Enter ArrowUp"
                  aria-haspopup="menu"
                  aria-expanded={sheetIsLogout}
                  type="button"
                  className="flex flex-col items-center justify-center gap-1 min-h-11 min-w-11 sm:min-w-16 transition-all duration-200 text-muted-foreground hover:text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl p-1 sm:px-3 select-none [-webkit-touch-callout:none]"
                >
                  <LogOut className="w-5 h-5" aria-hidden="true" />
                  <span className="text-[10px] font-body font-medium">Sair</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={8} className="max-w-[220px] text-center">
                <div className="flex items-center justify-center gap-2">
                  <p className="font-semibold text-xs">Sair</p>
                  <kbd className="hidden sm:inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                    Alt+0
                  </kbd>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">Encerra sua sessão atual e volta para a tela inicial.</p>
                <p className="text-[10px] text-muted-foreground/80 mt-1">Toque e segure para ações rápidas</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </nav>

      <Sheet open={activeSheet !== null} onOpenChange={(open) => { if (!open) closeSheet(); }}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl pb-safe"
          onOpenAutoFocus={(e) => {
            // Foco vai direto para a primeira ação do menu, não para o botão de fechar.
            e.preventDefault();
            const first = menuListRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]');
            first?.focus();
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            closeSheet();
          }}
        >
          {sheetItem && (
            <>
              <SheetHeader className="text-left">
                <SheetTitle className="flex items-center gap-2">
                  <sheetItem.icon className="w-5 h-5 text-primary" aria-hidden="true" />
                  Ações rápidas — {sheetItem.label}
                </SheetTitle>
                <SheetDescription>{sheetItem.hint}</SheetDescription>
              </SheetHeader>
              <ul
                ref={menuListRef}
                className="mt-4 flex flex-col gap-1"
                role="menu"
                aria-label={`Ações rápidas de ${sheetItem.label}`}
                onKeyDown={handleMenuKeyDown}
              >
                {sheetItem.actions.map((action) => (
                  <li key={action.label} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={async () => {
                        sheetOpenerRef.current = null;
                        setActiveSheet(null);
                        await action.onSelect(navigate);
                      }}
                      className="w-full flex items-start gap-3 rounded-lg p-3 min-h-11 text-left hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <action.icon className="w-4 h-4" aria-hidden="true" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-foreground">{action.label}</span>
                        <span className="block text-xs text-muted-foreground mt-0.5">{action.description}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {sheetIsLogout && (
            <>
              <SheetHeader className="text-left">
                <SheetTitle className="flex items-center gap-2">
                  <LogOut className="w-5 h-5 text-destructive" aria-hidden="true" />
                  Ações rápidas — Sair
                </SheetTitle>
                <SheetDescription>Encerre sua sessão ou apenas atualize seus dados.</SheetDescription>
              </SheetHeader>
              <ul
                ref={menuListRef}
                className="mt-4 flex flex-col gap-1"
                role="menu"
                aria-label="Ações rápidas de Sair"
                onKeyDown={handleMenuKeyDown}
              >
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={async () => {
                      sheetOpenerRef.current = null;
                      setActiveSheet(null);
                      await handleLogout();
                    }}
                    className="w-full flex items-start gap-3 rounded-lg p-3 min-h-11 text-left hover:bg-destructive/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
                  >
                    <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-destructive/10 text-destructive flex items-center justify-center">
                      <LogOut className="w-4 h-4" aria-hidden="true" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-foreground">Sair agora</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">Encerra sua sessão e volta para a tela inicial.</span>
                    </span>
                  </button>
                </li>
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      sheetOpenerRef.current = null;
                      setActiveSheet(null);
                      navigate("/profile?tab=settings");
                    }}
                    className="w-full flex items-start gap-3 rounded-lg p-3 min-h-11 text-left hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <Settings className="w-4 h-4" aria-hidden="true" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-foreground">Configurações da conta</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">Ajustar privacidade e preferências antes de sair.</span>
                    </span>
                  </button>
                </li>
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      sheetOpenerRef.current = null;
                      setActiveSheet(null);
                      window.location.reload();
                    }}
                    className="w-full flex items-start gap-3 rounded-lg p-3 min-h-11 text-left hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <RefreshCw className="w-4 h-4" aria-hidden="true" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-foreground">Recarregar app</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">Atualiza dados sem encerrar a sessão.</span>
                    </span>
                  </button>
                </li>
              </ul>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
});

BottomNav.displayName = "BottomNav";

export default BottomNav;
